/** SettingsStore — ユーザー設定を localStorage で永続化。
 *  音量(SFX/BGM 別)、モーション軽減、言語(JP/EN 切替の土台)。
 *  Reference: WCAG prefers-reduced-motion、Apple HIG / Material aria-* 準拠。
 *
 *  Callers:
 *    - web/src/main.ts: 起動時に load() → SoundManager / matchMedia と接続
 *    - web/src/ui/screens/HomeScreen.ts: 設定モーダルから get/set
 */

export type Language = 'ja' | 'en';

export interface Settings {
  /** SFX 音量 0..1。デフォルト 1.0 */
  sfxVolume: number;
  /** BGM 音量 0..1。デフォルト 1.0 */
  bgmVolume: number;
  /** ミュート(SFX/BGM 両方) */
  muted: boolean;
  /** ユーザー強制モーション軽減(prefers-reduced-motion とは独立のオーバーライド) */
  reducedMotion: boolean;
  /** 言語(現状 JP のみ、EN は土台のみ) */
  language: Language;
}

const STORAGE_KEY = 'ck.settings';

const DEFAULT_SETTINGS: Settings = {
  sfxVolume: 1.0,
  bgmVolume: 0.7,
  muted: false,
  reducedMotion: false,
  language: 'ja',
};

type Listener = (settings: Readonly<Settings>) => void;

export class SettingsStore {
  private state: Settings;
  private listeners: Listener[] = [];

  constructor() {
    this.state = this.load();
  }

  private load(): Settings {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return { ...DEFAULT_SETTINGS };
      const parsed = JSON.parse(raw) as Partial<Settings>;
      return {
        sfxVolume: clamp01(parsed.sfxVolume ?? DEFAULT_SETTINGS.sfxVolume),
        bgmVolume: clamp01(parsed.bgmVolume ?? DEFAULT_SETTINGS.bgmVolume),
        muted: !!parsed.muted,
        reducedMotion: !!parsed.reducedMotion,
        language: parsed.language === 'en' ? 'en' : 'ja',
      };
    } catch {
      return { ...DEFAULT_SETTINGS };
    }
  }

  private save(): void {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state)); } catch { /* noop */ }
    for (const l of this.listeners) l(this.state);
  }

  get(): Readonly<Settings> {
    return this.state;
  }

  setSfxVolume(v: number): void {
    this.state.sfxVolume = clamp01(v);
    this.save();
  }

  setBgmVolume(v: number): void {
    this.state.bgmVolume = clamp01(v);
    this.save();
  }

  setMuted(m: boolean): void {
    this.state.muted = m;
    this.save();
  }

  setReducedMotion(m: boolean): void {
    this.state.reducedMotion = m;
    // 即時 DOM 反映:body にクラスを付ける(CSS 側で `.cmf-reduced-motion *` でアニメ無効化)
    if (typeof document !== 'undefined') {
      document.body.classList.toggle('cmf-reduced-motion', m);
    }
    this.save();
  }

  setLanguage(l: Language): void {
    this.state.language = l;
    if (typeof document !== 'undefined') {
      document.documentElement.lang = l;
    }
    this.save();
  }

  /** 設定変更を購読(SoundManager 等が音量変更を即反映するため) */
  subscribe(listener: Listener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }
}

function clamp01(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(1, v));
}
